import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useValidateCohortDefinition } from './useValidateCohortDefinition';
import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../../../../contexts/CohortBuilderModalContext';

export const useTriggerExitModal = () => {
  const navigate = useNavigate();

  const { hasUnsavedChanges } = useValidateCohortDefinition();
  const { openDialog, closeDialog } = useCohortBuilderModalContext();

  const onTriggerExitModal = useCallback(
    (action: () => void, isUnloading?: boolean) => {
      if (hasUnsavedChanges && !isUnloading) {
        openDialog(CohortBuilderActionDialogType.EXIT_COHORT_DEFINITION, {
          exitAction: () => {
            action();
            closeDialog();
          },
        });
      } else {
        action();
        closeDialog();
      }
    },
    [closeDialog, hasUnsavedChanges, openDialog]
  );

  const handleKeyboardAction = useCallback(
    (event: KeyboardEvent) => {
      // If the user is in an input field, don't trigger the exit modal
      const activeElement = document.activeElement as HTMLElement;
      const isInputField =
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable;

      if (isInputField) {
        return;
      }

      if (event.metaKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        onTriggerExitModal(() => {
          navigate(-1);
        }, false);
      } else if (event.metaKey && event.key === 'ArrowRight') {
        event.preventDefault();
        onTriggerExitModal(() => {
          navigate(1);
        }, false);
      }
    },
    [navigate, onTriggerExitModal]
  );

  // TODO: handle browser back/forward buttons

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        onTriggerExitModal(() => {
          // do nothing and instead use the browser's default confirmation dialog
        }, true);
      }
    };

    window.addEventListener('keydown', handleKeyboardAction);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyboardAction);
    };
  }, [handleKeyboardAction, hasUnsavedChanges, navigate, onTriggerExitModal]);

  return { onTriggerExitModal };
};
